import { Clock, TrendingUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import SectionHeading from "../../SectionHeading";
import Eyebrow from "../../Eyebrow";
import MetaText from "../../MetaText";
import GlobalHeadline from "../../../GlobalHeadline";
import MetricValue from "../../../MetricValue";
import Badge from "../../../Badge";

import SpotlightCard from "../../SpotlightCard";
import AnimatedCounter from "../../AnimatedCounter";
import { siteSections } from "../../../../content/siteSections";

/**
 * Private equity dashboard section with animated metrics.
 * Translation: Private equity dashboard section with animated metrics.
 *
 * @returns {JSX.Element} Private equity section.
 */
const PrivateEquity = () => {
  const sectionRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const viewHeight = window.innerHeight;
      // Calculate progress through the section relative to viewport
      let progress = (viewHeight - rect.top) / (rect.height + viewHeight);
      progress = Math.max(0, Math.min(1, (progress - 0.2) / 0.6));
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsActive(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect();
    };
  }, []);

  const donutCircumference = 263.9;
  const donutValue = siteSections.privateEquity.cards[0].target * scrollProgress;
  const donutOffset =
    donutCircumference -
    (donutValue / 100) * donutCircumference;

  return (
    <section
      id="pe"
      ref={sectionRef}
      className={`section-block section-block--pe ${isActive ? "is-active" : ""}`}
    >
      <div className="section-shell">
        <div className="pe-section__hero">
          <SectionHeading
            eyebrow={siteSections.privateEquity.eyebrow}
            title={
              <>
                We speak the language of{" "}
                <span className="text-accent">
                  {siteSections.privateEquity.titleAccent}
                </span>
                .
              </>
            }
            description={siteSections.privateEquity.description}
            variant="fade-right"
          />
        </div>

        <div className="pe-grid pe-grid--hero">
          <SpotlightCard className="glass-panel pe-card pe-card--metric">
            <AnimatedCounter
              as="span"
              target={siteSections.privateEquity.headlineMetric.value}
              prefix={siteSections.privateEquity.headlineMetric.prefix}
              suffix={siteSections.privateEquity.headlineMetric.suffix}
              className="pe-card__headline-value text-metric"
            />
            <Eyebrow className="pe-card__headline-label">
              {siteSections.privateEquity.headlineMetric.label}
            </Eyebrow>
            <p>{siteSections.privateEquity.headlineMetric.description}</p>
          </SpotlightCard>

          <SpotlightCard className="glass-panel pe-card pe-card--bars">
            <div className="pe-speed">
              <div className="pe-speed__header">
                <Eyebrow className="pe-card__headline-label">{siteSections.privateEquity.speedToValue.label}</Eyebrow>
                <Badge className="pe-speed__badge">
                  {siteSections.privateEquity.speedToValue.badge}
                </Badge>
              </div>
              <GlobalHeadline as="h3">
                {siteSections.privateEquity.speedToValue.headline}
              </GlobalHeadline>
              <p>{siteSections.privateEquity.speedToValue.description}</p>
            </div>

            <div className="pe-speed__bars">
              {siteSections.privateEquity.speedToValue.bars.map((bar) => (
                <div key={bar.label} className="pe-speed__bar-group">
                  <div className="pe-speed__bar-labels">
                    <MetaText className={bar.accent ? "text-accent" : ""}>{bar.label}</MetaText>
                    <MetaText className={bar.accent ? "text-accent" : ""}>{bar.suffix}</MetaText>
                  </div>
                  <div className="pe-speed__bar-track">
                    <div
                      className={`pe-speed__bar-fill ${bar.accent ? "is-accent" : ""}`}
                      style={{ width: `${bar.base + (bar.target - bar.base) * scrollProgress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </SpotlightCard>
        </div>

        <div className="pe-grid pe-grid--secondary">
          <SpotlightCard className="glass-panel pe-card pe-card--ebitda">

            <div className="mb-6">
              <Eyebrow className="pe-card__headline-label">{siteSections.privateEquity.cards[0].eyebrow}</Eyebrow>
              <GlobalHeadline as="h3">{siteSections.privateEquity.cards[0].title}</GlobalHeadline>
            </div>
            <div className="pe-card--ebitda__body">
              <div className="pe-donut shrink-0">
                <svg viewBox="0 0 100 100" className="pe-donut__chart" aria-hidden="true">
                  <circle cx="50" cy="50" r="42" className="pe-donut__track" />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    className="pe-donut__progress"
                    strokeDasharray={donutCircumference}
                    strokeDashoffset={donutOffset}
                  />
                </svg>
                <span className="pe-donut__value text-metric" style={{ fontSize: '1.25rem' }}>
                  +{donutValue.toFixed(1)}%
                </span>
              </div>
              <p>{siteSections.privateEquity.cards[0].description}</p>
            </div>
            <div className="mt-auto pt-6">
              <Eyebrow className="pe-card__status">{siteSections.privateEquity.cards[0].footer}</Eyebrow>
            </div>
          </SpotlightCard>

          <SpotlightCard className="glass-panel pe-card">
            <div className="pe-card--governance-content">
              <div className="pe-card__icon-wrapper">
                <TrendingUp className="w-5 h-5" strokeWidth={1.25} />
              </div>
              <div className="mb-8">
                <MetricValue>{siteSections.privateEquity.cards[1].displayTarget}%</MetricValue>
                <Eyebrow className="pe-card__headline-label">
                  {siteSections.privateEquity.cards[1].eyebrow}
                </Eyebrow>
              </div>
              <p>{siteSections.privateEquity.cards[1].description}</p>
            </div>
          </SpotlightCard>

          <SpotlightCard className="glass-panel pe-card pe-card--realtime">
            <div className="pe-card__icon-wrapper pe-card__icon-wrapper--top-right">
              <Clock className="w-5 h-5" strokeWidth={1.25} />
            </div>
            <div className="pe-card--realtime-content h-full flex flex-col">
              <div className="mt-8 text-right">
                <MetricValue as="h3" className="pe-card__title-massive">
                  {siteSections.privateEquity.cards[2].headline}
                </MetricValue>
                <Eyebrow className="pe-card__headline-label">
                  {siteSections.privateEquity.cards[2].eyebrow}
                </Eyebrow>
              </div>
              <div className="mt-12 flex-grow flex flex-col justify-center items-end">
                <p className="text-right max-w-[280px] leading-relaxed opacity-80">
                  {siteSections.privateEquity.cards[2].description}
                </p>
              </div>
              <div className="mt-auto text-right">
                <Eyebrow className="pe-card__status pe-card--status-active">
                  {siteSections.privateEquity.cards[2].footer}
                </Eyebrow>
              </div>
            </div>
          </SpotlightCard>
        </div>

        <div className="pe-grid pe-grid--footer">
          <SpotlightCard className="glass-panel pe-card pe-card--board">
            <div className="pe-card--board__content">
              <div className="pe-card--board__text">
                <Eyebrow className="pe-card__headline-label">{siteSections.privateEquity.boardReadiness.eyebrow}</Eyebrow>
                <GlobalHeadline as="h3">
                  {siteSections.privateEquity.boardReadiness.title}
                </GlobalHeadline>
                <p className="pe-card__deliverable-description">{siteSections.privateEquity.boardReadiness.description}</p>
                <div className="mt-10">
                  <MetaText className="pe-card__status">
                    {siteSections.privateEquity.boardReadiness.footer}
                  </MetaText>
                </div>
              </div>

              <div className="pe-card--board__visual">
                <svg viewBox="0 0 160 100" className="pe-board-graphic" aria-hidden="true">
                  <circle cx="20" cy="20" r="2.5" className="pe-board-graphic__node" opacity="0.6" />
                  <circle cx="20" cy="50" r="2.5" className="pe-board-graphic__node" opacity="0.6" />
                  <circle cx="20" cy="80" r="2.5" className="pe-board-graphic__node" opacity="0.6" />
                  <path d="M25 20 L130 50" className="pe-board-graphic__line" />
                  <path d="M25 50 L130 50" className="pe-board-graphic__line" />
                  <path d="M25 80 L130 50" className="pe-board-graphic__line" />

                  {/* Data packets moving along lines */}
                  <circle r="1.5" fill="var(--color-blue-400)">
                    <animateMotion dur="3s" repeatCount="indefinite" path="M25 20 L130 50" />
                  </circle>
                  <circle r="1.5" fill="var(--color-blue-400)">
                    <animateMotion dur="2.5s" repeatCount="indefinite" path="M25 50 L130 50" />
                  </circle>
                  <circle r="1.5" fill="var(--color-blue-400)">
                    <animateMotion dur="3.5s" repeatCount="indefinite" path="M25 80 L130 50" />
                  </circle>

                  <circle cx="135" cy="50" r="6" className="pe-board-graphic__hub" />
                  <circle cx="135" cy="50" r="12" className="pe-board-graphic__halo" opacity="0.4" />
                </svg>
              </div>
            </div>
          </SpotlightCard>

          <SpotlightCard className="glass-panel pe-card pe-card--transfer">
            <div className="pe-transfer__header">
              <div>
                <Eyebrow className="pe-card__headline-label">
                  {siteSections.privateEquity.exitReadiness.eyebrow}
                </Eyebrow>
                <GlobalHeadline as="h3">
                  {siteSections.privateEquity.exitReadiness.title}
                </GlobalHeadline>
              </div>

              <div className="pe-transfer__status">
                <MetaText>{siteSections.privateEquity.exitReadiness.status}</MetaText>
                <div className="pe-transfer__bars">
                  <span />
                  <span />
                  <span className="is-active" />
                </div>
              </div>
            </div>

            <p className="pe-card__deliverable-description">{siteSections.privateEquity.exitReadiness.description}</p>

            <div className="pe-transfer__footer">
              <div className="pe-transfer__rail">
                <MetaText>{siteSections.privateEquity.exitReadiness.labels[0]}</MetaText>
                <div className="pe-transfer__track">
                  <div className={`pe-transfer__scan ${isActive ? "is-active" : ""}`} />
                </div>
                <MetaText>{siteSections.privateEquity.exitReadiness.labels[1]}</MetaText>
              </div>

              <div className="pe-transfer__footnotes">
                {siteSections.privateEquity.exitReadiness.footnotes.map((item) => (
                  <MetaText key={item}>{item}</MetaText>
                ))}
              </div>
            </div>
          </SpotlightCard>
        </div>
      </div>
    </section >
  );
};

export default PrivateEquity;
